// Кредитното известие към софтуерна фактура (ЗДДС чл. 115) — въпросът „Да
// отпечатам ли?" веднага след като BE го е издал, и печатът му на POS
// (`POST /invoices/{id}/print` — като всяка фактура: ОРИГИНАЛ за клиента и
// КОПИЕ, после само дубликати).
//
// BE издава известието само: „Сторнирай всичко" на сметка със софтуерна
// фактура (продажбата е развалена — сторно бон) и „Анулирай" на депозит с
// фактура (върнат аванс). Тогава фактурата НЕ се анулира и протокол за
// анулиране няма (`useInvoiceCancellationProtocol` е за чл. 116 — „Анулирай" и
// „Възстанови"). По-късно известието се печата от „Справки" → „Фактури".
//
// Въпросът е `modal.confirm` на App-а — прозорецът на действието се затваря
// веднага, а въпросът трябва да остане. `message` подава екранът (както при
// протокола).

import { useState } from "react";
import { App } from "antd";
import { PrinterOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { printInvoicePos } from "../api/invoices";
import { ConfirmConsequences } from "../components/ConfirmConsequences";
import { usePrinterNotifications } from "./usePrinterNotifications";
import { blockIfLicenseReadOnly } from "../lib/licenseReadOnly";
import { getApiErrorMessage } from "../utils/translations";

type SuccessMessage = { success: (content: string) => unknown };

/** Издаденото известие — `software_credit_note` на „Сторнирай всичко",
 *  `creditNote*` на анулирания депозит. */
export interface IssuedCreditNote {
  id: number;
  number: string;
  /** Номерът на кредитираната фактура. */
  invoiceNumber: string;
  /** Защо — сторнирана продажба или върнат аванс (текстът на въпроса). */
  cause: "storno" | "deposit";
}

export const useCreditNoteOffer = (message: SuccessMessage) => {
  const { t } = useTranslation();
  const { modal } = App.useApp();
  const { notifyPrinterError } = usePrinterNotifications();
  const [isPrinting, setIsPrinting] = useState(false);

  const print = async (creditNoteId: number) => {
    if (blockIfLicenseReadOnly()) return;
    setIsPrinting(true);
    try {
      await printInvoicePos(creditNoteId);
      message.success(t("creditNoteOffer.printSuccess"));
    } catch (error: unknown) {
      notifyPrinterError(getApiErrorMessage(error, t("creditNoteOffer.printError")));
    } finally {
      setIsPrinting(false);
    }
  };

  const offer = (note: IssuedCreditNote) => {
    modal.confirm({
      title: t("creditNoteOffer.title", { number: note.number, invoice: note.invoiceNumber }),
      icon: <PrinterOutlined className="!text-pos-accent" />,
      content: (
        <ConfirmConsequences
          lines={[
            {
              text: t(note.cause === "deposit" ? "creditNoteOffer.whyDeposit" : "creditNoteOffer.why", {
                invoice: note.invoiceNumber,
              }),
            },
            { text: t("creditNoteOffer.copies") },
            { text: t("creditNoteOffer.later") },
          ]}
          question={t("creditNoteOffer.question")}
        />
      ),
      okText: t("creditNoteOffer.print"),
      cancelText: t("creditNoteOffer.notNow"),
      centered: true,
      okButtonProps: { className: "!border-pos-accent !bg-pos-accent" },
      onOk: () => print(note.id),
    });
  };

  /** Групова фактура: известие не е издадено — постоянно предупреждение. */
  const warnGroupInvoice = (invoiceNumber: string, otherBillIds: readonly number[]) => {
    modal.warning({
      title: t("creditNoteOffer.groupTitle", { invoice: invoiceNumber }),
      content: t("creditNoteOffer.groupText", { invoice: invoiceNumber, bills: otherBillIds.join(", ") }),
      centered: true,
    });
  };

  return { offer, print, isPrinting, warnGroupInvoice };
};
